import { getRandomInt } from "../Shared/Helpers.tsx";
import { generateProblemPages } from "../Shared/Generation/problemPages.ts";
import { formatScaled, rescale, trailingPlaces } from "../Shared/scaledDecimal.ts";
import { Operations, ProblemData } from "./Constants.ts";
import { ISettingsProps } from "./Models.ts";

/**
 * Decimal problem generation.
 *
 * Every value is an integer scaled by 10^places - 3.45 at two places is 345 -
 * so the arithmetic is exact and never accumulates the error of adding 3.45 to
 * 2.18 in binary floating point. The point is inserted on the way to the page,
 * through ProblemData.display; nothing here ever calls parseFloat.
 *
 * The scaled-integer primitive is shared with MoneyGenerator, in
 * Shared/scaledDecimal.ts. Only that primitive is shared: money is always two
 * places with a dollar sign and no division, decimals are one to three places,
 * no prefix, and division is included. See docs/adr/0004-decimal-worksheets.md.
 *
 * Layout is the ordinary vertical one - a decimal problem is a column
 * arithmetic problem that happens to have a point in it - so there is no
 * renderer here, only a row-width entry in PdfGenerator/Page.tsx.
 *
 * That layout right-aligns on the last digit, which is why the two operands of
 * an addition or subtraction are printed padded to a common number of places:
 * 3.91 over 0.60, never 3.91 over 0.6. Printed ragged, the points do not line
 * up and the problem on the page is not the problem the answer key answers.
 * The place counts are still drawn independently, so the padding is visible and
 * the sheet mixes one-, two- and three-place problems - but the alignment
 * lesson itself needs a horizontal layout. See ADR 0004 section 2, as amended.
 */

interface Decimal {
    /** Value as an integer scaled by 10^places. */
    scaled: number;
    places: number;
}

export const DECIMAL_MIN_PLACES = 1;
export const DECIMAL_MAX_PLACES = 3;

function clampPlaces(places: number): number {
    if (places < DECIMAL_MIN_PLACES) return DECIMAL_MIN_PLACES;
    if (places > DECIMAL_MAX_PLACES) return DECIMAL_MAX_PLACES;
    return places;
}

/**
 * A positive decimal whose place count is drawn independently, one up to the
 * configured maximum, per operand and per problem.
 *
 * The draw is what varies a sheet: problems land at one, two or three places
 * rather than all at the maximum, and an operand drawn short is printed padded
 * to its partner's width, so values ending in zero appear naturally.
 */
function randomDecimal(maxPlaces: number, maxWhole: number): Decimal {
    const places = getRandomInt(DECIMAL_MIN_PLACES, maxPlaces);
    const scaled = getRandomInt(1, Math.max(1, maxWhole) * 10 ** places);
    return { scaled, places };
}

/** [top, bottom, answer] as formatted strings, ready for the vertical layout. */
function display(first: string, second: string, answer: string): string[] {
    return [first, second, answer];
}

/**
 * One decimal problem, or null when the draw is not worth printing - a
 * subtraction of two equal values, whose answer is always zero.
 */
export function buildDecimalProblem(
    operator: Operations,
    maxPlaces: number,
    maxWhole: number,
    key: number
): ProblemData | null {
    const places = clampPlaces(maxPlaces);

    if (operator === Operations.Multiplication) {
        // Decimal x decimal doubles the place count, which no elementary
        // curriculum asks for - so it is only offered when the maximum is one
        // place, where the product lands at two places and 0.4 x 0.7 = 0.28 is
        // a real problem. Otherwise it is decimal x whole number.
        if (places === 1 && getRandomInt(0, 1) === 0) {
            const a = getRandomInt(1, Math.max(1, maxWhole) * 10);
            const b = getRandomInt(1, 9);
            const product = a * b;
            return {
                key,
                numbers: [a, b, product],
                operator: "x",
                decimalPlaces: 2,
                display: display(formatScaled(a, 1), formatScaled(b, 1), formatScaled(product, 2)),
            };
        }

        const amount = randomDecimal(places, maxWhole);
        const multiplier = getRandomInt(2, 9);
        const product = amount.scaled * multiplier;
        return {
            key,
            numbers: [amount.scaled, multiplier, product],
            operator: "x",
            decimalPlaces: amount.places,
            display: display(
                formatScaled(amount.scaled, amount.places),
                String(multiplier),
                formatScaled(product, amount.places)
            ),
        };
    }

    if (operator === Operations.Division || operator === Operations.LongDivision) {
        // Built backwards from a chosen quotient and a whole-number divisor, so
        // the division always terminates. Dividing by a decimal is a different
        // lesson and out of scope (ADR 0004).
        //
        // The divisor is drawn first and the quotient bounded by it, because the
        // dividend is what gets printed: drawing the quotient freely and
        // multiplying put 179.01 on a sheet whose setting says "largest whole
        // number: 20".
        const quotientPlaces = getRandomInt(DECIMAL_MIN_PLACES, places);
        const divisor = getRandomInt(2, 9);
        const largestQuotient = Math.floor(
            (Math.max(1, maxWhole) * 10 ** quotientPlaces) / divisor
        );
        const quotientScaled = getRandomInt(1, Math.max(1, largestQuotient));
        const dividend = quotientScaled * divisor;
        // The answer prints at the places it actually needs, not padded: 4.5 / 3
        // is 1.5, and 1.50 would imply a precision the problem does not have.
        // This is the one exception to keeping trailing zeros (ADR 0004 s4).
        const answerPlaces = trailingPlaces(quotientScaled, quotientPlaces);
        const answerScaled = rescale(quotientScaled, quotientPlaces, answerPlaces);
        return {
            key,
            numbers: [dividend, divisor, quotientScaled],
            operator: "÷",
            decimalPlaces: quotientPlaces,
            display: display(
                formatScaled(dividend, quotientPlaces),
                String(divisor),
                formatScaled(answerScaled, answerPlaces)
            ),
        };
    }

    let first = randomDecimal(places, maxWhole);
    let second = randomDecimal(places, maxWhole);
    const common = Math.max(first.places, second.places);
    let firstCommon = rescale(first.scaled, first.places, common);
    let secondCommon = rescale(second.scaled, second.places, common);

    if (operator === Operations.Subtraction) {
        if (secondCommon > firstCommon) {
            [first, second] = [second, first];
            [firstCommon, secondCommon] = [secondCommon, firstCommon];
        }
        if (firstCommon === secondCommon) {
            return null;
        }
        const difference = firstCommon - secondCommon;
        return {
            key,
            numbers: [firstCommon, secondCommon, difference],
            operator: "-",
            decimalPlaces: common,
            // Both operands at the common scale, so the points line up under a
            // renderer that right-aligns on the last digit. See the file header.
            display: display(
                formatScaled(firstCommon, common),
                formatScaled(secondCommon, common),
                formatScaled(difference, common)
            ),
        };
    }

    const sum = firstCommon + secondCommon;
    return {
        key,
        numbers: [firstCommon, secondCommon, sum],
        operator: "+",
        decimalPlaces: common,
        display: display(
            formatScaled(firstCommon, common),
            formatScaled(secondCommon, common),
            formatScaled(sum, common)
        ),
    };
}

/** Addition, subtraction, multiplication and division. */
function usableOperators(settings: ISettingsProps): Operations[] {
    const selected = settings.Operators?.map((operator) => operator.value) ?? [];
    const usable = selected.map((operator) =>
        operator === Operations.LongDivision ? Operations.Division : operator
    );
    const deduped = Array.from(new Set(usable)).filter(
        (operator) =>
            operator === Operations.Addition ||
            operator === Operations.Subtraction ||
            operator === Operations.Multiplication ||
            operator === Operations.Division
    );
    return deduped.length > 0 ? deduped : [Operations.Addition];
}

/** A page-shaped set of decimal problems, matching what GenerateDataV2 returns. */
export function generateDecimalPages(settings: ISettingsProps): ProblemData[][] | null {
    if (!settings.Operators || settings.Operators.length === 0) {
        return null;
    }

    const operators = usableOperators(settings);
    const maxPlaces = clampPlaces(settings.DecimalPlaces ?? 2);
    const maxWhole = settings.DecimalMaxWhole ?? 20;
    return generateProblemPages({
        settings,
        choices: operators,
        build: (operator, key) => buildDecimalProblem(operator, maxPlaces, maxWhole, key),
        fingerprint: (problem) => problem.display!.slice(0, 2).join(problem.operator),
    });
}
